import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, GitMerge, Scissors, Upload } from "lucide-react";

import { useModalContext } from "../components/ModalProvider";
import { useAdministrationAPI } from "../AdministrationAPI";
import { ProductSeriesDetail, ProductSeriesRow, useProductSeriesAPI } from "../ProductSeriesAPI";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

/** An enumeration as the server splits it — comma, semicolon, „und“, „and“, „&“; never a slash. */
export const looksLikeEnumeration = (name: string): boolean =>
  String(name ?? "")
    .split(/\s*[,;]\s*|\s+(?:und|and|&)\s+/i)
    .filter((part) => part.trim()).length > 1;

/**
 * Product series of the shared catalogue (tri-server docs/product-series-entwurf.md): confirm, rename, merge two series
 * of one maker, split an enumeration, attach series documents. Platform root only — the routes refuse everyone else.
 */
export default function ProductSeriesPanel() {
  const { t } = useTranslation();
  const api = useProductSeriesAPI();
  const { uploadEntityDocuments } = useAdministrationAPI();
  const { pushError } = useModalContext() as any;

  const [rows, setRows] = useState<ProductSeriesRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ProductSeriesDetail | null>(null);
  const [name, setName] = useState("");
  const [mergeQuery, setMergeQuery] = useState("");
  const [mergeCandidates, setMergeCandidates] = useState<ProductSeriesRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .list({ q: search.trim() || undefined, status: status === "all" ? undefined : status, limit: 100 })
      .then((r) => {
        if (cancelled) return;
        setRows(r.items);
        setTotal(r.total);
      })
      .catch((error) => !cancelled && pushError(error))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, reload]);

  useEffect(() => {
    if (!openId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    api
      .get(openId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setName(d.series?.name ?? "");
      })
      .catch((error) => !cancelled && pushError(error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, reload]);

  // Merge targets: series of the same maker, found by name.
  useEffect(() => {
    const maker = detail?.series?.manufacturer;
    if (!maker || mergeQuery.trim().length < 2) {
      setMergeCandidates([]);
      return;
    }
    let cancelled = false;
    api
      .list({ q: mergeQuery.trim(), manufacturer: String(maker), limit: 10 })
      .then((r) => !cancelled && setMergeCandidates(r.items.filter((s) => s._id !== detail?.series?._id)))
      .catch(() => !cancelled && setMergeCandidates([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergeQuery, detail]);

  const run = async (action: () => Promise<unknown>, message: string, close = false) => {
    try {
      await action();
      toast.success(message);
      if (close) setOpenId(null);
      setReload((n) => n + 1);
    } catch (error) {
      pushError(error);
    }
  };

  const evidenceLabel = (evidence?: string) =>
    evidence ? t(`assetPropertiesPanel.productSeries.evidence.${evidence}`, { defaultValue: evidence }) : "—";

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="text-sm text-muted-foreground">{t("productSeriesPanel.intro")}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="max-w-sm"
          placeholder={t("productSeriesPanel.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("productSeriesPanel.statusAll")}</SelectItem>
            <SelectItem value="derived">{t("productSeriesPanel.statusDerived")}</SelectItem>
            <SelectItem value="confirmed">{t("productSeriesPanel.statusConfirmed")}</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{t("productSeriesPanel.count", { total })}</span>
      </div>

      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("productSeriesPanel.columns.name")}</TableHead>
              <TableHead>{t("productSeriesPanel.columns.manufacturer")}</TableHead>
              <TableHead className="text-right">{t("productSeriesPanel.columns.products")}</TableHead>
              <TableHead className="text-right">{t("productSeriesPanel.columns.documents")}</TableHead>
              <TableHead>{t("productSeriesPanel.columns.evidence")}</TableHead>
              <TableHead>{t("productSeriesPanel.columns.status")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row._id} className="cursor-pointer" onClick={() => setOpenId(row._id)}>
                <TableCell className="max-w-xs break-words">{row.name}</TableCell>
                <TableCell>{row.manufacturer?.name || "—"}</TableCell>
                <TableCell className="text-right">{row.products}</TableCell>
                <TableCell className="text-right">{row.documents}</TableCell>
                <TableCell>{evidenceLabel(row.evidence)}</TableCell>
                <TableCell>
                  <Badge variant={row.status === "confirmed" ? "success" : "outline"}>
                    {row.status === "confirmed" ? t("productSeriesPanel.statusConfirmed") : t("productSeriesPanel.statusDerived")}
                  </Badge>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {row.status !== "confirmed" ? (
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => run(() => api.confirm(row._id), t("productSeriesPanel.confirmed"))}>
                      <Check className="h-4 w-4" />
                      {t("productSeriesPanel.confirm")}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!openId} onOpenChange={(open) => !open && setOpenId(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detail?.series?.name ?? "…"}</DialogTitle>
          </DialogHeader>
          {detail ? (
            <div className="flex flex-col gap-4 text-sm">
              <div className="text-muted-foreground">
                {evidenceLabel(detail.series.evidence)} · {t("productSeriesPanel.assetsBound", { count: detail.assets })}
              </div>

              <div className="flex flex-wrap items-end gap-2">
                <Input className="max-w-md" value={name} onChange={(e) => setName(e.target.value)} />
                <Button
                  variant="outline"
                  disabled={!name.trim() || name === detail.series.name}
                  onClick={() => run(() => api.update(detail.series._id, { name }), t("productSeriesPanel.renamed"))}
                >
                  {t("productSeriesPanel.rename")}
                </Button>
                {looksLikeEnumeration(detail.series.name) ? (
                  <Button variant="outline" className="gap-1" onClick={() => run(() => api.split(detail.series._id), t("productSeriesPanel.split"), true)}>
                    <Scissors className="h-4 w-4" />
                    {t("productSeriesPanel.splitAction")}
                  </Button>
                ) : null}
              </div>
              {(detail.series.aliases ?? []).length > 1 ? (
                <div className="text-muted-foreground">
                  {t("productSeriesPanel.aliases")}: {(detail.series.aliases ?? []).join(" · ")}
                </div>
              ) : null}

              <div>
                <div className="mb-1 font-semibold">{t("productSeriesPanel.mergeInto")}</div>
                <Input className="max-w-md" placeholder={t("productSeriesPanel.mergeSearch")} value={mergeQuery} onChange={(e) => setMergeQuery(e.target.value)} />
                <div className="mt-2 flex flex-col gap-1">
                  {mergeCandidates.map((candidate) => (
                    <Button
                      key={candidate._id}
                      variant="ghost"
                      className="justify-start gap-2"
                      onClick={() => run(() => api.merge(detail.series._id, candidate._id), t("productSeriesPanel.merged"), true)}
                    >
                      <GitMerge className="h-4 w-4" />
                      {candidate.name} ({candidate.products})
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center gap-2 font-semibold">
                  {t("productSeriesPanel.documents")}
                  <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-normal text-primary">
                    <Upload className="h-3 w-3" />
                    {t("productSeriesPanel.upload")}
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (file) {
                          run(() => uploadEntityDocuments("ProductSeries", detail.series._id, [{ file, role: "maintenance" }]), t("productSeriesPanel.uploaded"));
                        }
                      }}
                    />
                  </label>
                </div>
                {detail.documents.length ? (
                  <ul className="list-disc pl-5">
                    {detail.documents.map((d) => (
                      <li key={d._id}>
                        {d.name} {d.roles.length ? <span className="text-muted-foreground">({d.roles.join(", ")})</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-muted-foreground">{t("productSeriesPanel.noDocuments")}</div>
                )}
              </div>

              <div>
                <div className="mb-1 font-semibold">{t("productSeriesPanel.products", { count: detail.products.length })}</div>
                <ul className="max-h-64 list-disc overflow-y-auto pl-5">
                  {detail.products.map((p) => (
                    <li key={p._id}>
                      {p.name}
                      {p.model && p.model !== p.name ? <span className="text-muted-foreground"> · {p.model}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <Skeleton className="h-40 w-full" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
